/**
 * The in-game RUNNER. Primary art is the brand pack's game-camera sprite atlases (brand/exports/sprites: 12 states,
 * 12 frames each, articulated rig renders — the character never slides as a static bitmap); `drawRunnerSprite` draws
 * one frame anchored at the pack's ground reference. `drawRunner` is the procedural articulated fallback used until
 * the atlases decode (and in jsdom, where images never load).
 */
import { color } from '@runner/ui';
import { runnerEffect, runnerSprite, brandImage, type SpriteState } from './brandArt';
import { launchInfo } from '../data/launch';

export type Skin = 'runner' | 'crab' | 'roadster';
export const SKINS: readonly Skin[] = ['runner', 'crab', 'roadster'];
export const isSkin = (v: unknown): v is Skin => SKINS.includes(v as Skin);

/** ?skin=crab for previews and renders; it outranks the viewer's own pick (prefs.tsx). */
export const urlSkin: Skin | null = (() => {
  const v = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('skin') : null;
  return isSkin(v) ? v : null;
})();

// The viewer's pick (set by PrefsProvider), else the event default from /launch.json for pages without prefs.
let skin: Skin | undefined = urlSkin ?? undefined;
if (!skin && typeof fetch === 'function') launchInfo().then((i) => { if (isSkin(i?.skin)) skin ??= i.skin; });
export function setRunnerSkin(s: Skin) { skin = s; }

/**
 * The crab skin, drawn procedurally (no asset): rear-overhead view, eight legs scuttling in alternating pairs,
 * claws raised and snapping, stalk eyes. Same 256-unit frame and ground reference as the runner atlases.
 */
function drawCrab(ctx: CanvasRenderingContext2D, state: SpriteState, frame: number, size: number, opts: { effect: boolean; shadow: boolean }): boolean {
  const k = (size / 256) * 0.62; // drawn wide in 256 units; scaled to the runner's footprint so obstacles still read right
  const phase = (frame / 12) * Math.PI * 2;
  const busy = state === 'idle' || state === 'waiting' ? 0.3 : 1;
  const bob = Math.abs(Math.sin(phase * 2)) * 5 * k * busy;
  const tilt = state === 'steer-left' ? -0.14 : state === 'steer-right' ? 0.14 : state === 'stumble' ? 0.2 * Math.sin(phase * 2) : 0;
  const ink = '#0B0B0B', shell = '#EC673A', cream = '#FFF2D8';
  if (opts.shadow) {
    ctx.fillStyle = 'rgba(11,11,11,0.28)';
    ctx.beginPath();
    ctx.ellipse(0, 0, 96 * k, 16 * k, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.save();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  // Inked limb: a thick ink stroke under a thinner shell-coloured one, like the brand's outlined art.
  const limb = (pts: [number, number][], w: number) => {
    for (const [c, lw] of [[ink, w + 7], [shell, w]] as const) {
      ctx.strokeStyle = c;
      ctx.lineWidth = lw * k;
      ctx.beginPath();
      pts.forEach(([x, y], i) => (i ? ctx.lineTo(x * k, y * k) : ctx.moveTo(x * k, y * k)));
      ctx.stroke();
    }
  };
  // Legs stay planted on the ground while the body bobs and tilts above them.
  for (const side of [-1, 1]) {
    for (let i = 0; i < 4; i++) {
      const lift = Math.max(0, Math.sin(phase * 2 + i * 1.6 + (side > 0 ? Math.PI : 0))) * 10 * busy;
      limb([[side * (34 + i * 4), -50 + i * 9 - bob / k], [side * (74 + i * 6), -64 + i * 10 - lift], [side * (90 + i * 6), -6 + i * 2 - lift * 0.6]], 7);
    }
  }
  ctx.translate(0, -bob);
  ctx.rotate(tilt);
  // Claws: arms up and out, pincers snapping open and shut.
  const snap = (0.18 + 0.22 * Math.abs(Math.sin(phase * (busy < 1 ? 1 : 2)))) * (state === 'stumble' ? 1.6 : 1);
  for (const side of [-1, 1]) {
    limb([[side * 38, -84], [side * 62, -108], [side * 64, -126]], 10);
    ctx.save();
    ctx.translate(side * 66 * k, -138 * k);
    for (const [rot, rx, ry] of [[-snap, 22, 15], [snap * 1.4, 13, 9]] as const) {
      ctx.save();
      ctx.rotate(side * rot);
      ctx.translate(0, rot < 0 ? -4 * k : 6 * k);
      ctx.fillStyle = shell;
      ctx.strokeStyle = ink;
      ctx.lineWidth = 6 * k;
      ctx.beginPath();
      ctx.ellipse(side * 4 * k, 0, rx * k, ry * k, side * 0.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }
    ctx.restore();
  }
  // Eye stalks and the runner's big white eyes.
  for (const side of [-1, 1]) {
    ctx.strokeStyle = ink;
    ctx.lineWidth = 7 * k;
    ctx.beginPath();
    ctx.moveTo(side * 16 * k, -92 * k);
    ctx.lineTo(side * 20 * k, -118 * k);
    ctx.stroke();
    ctx.fillStyle = cream;
    ctx.lineWidth = 5 * k;
    ctx.beginPath();
    ctx.arc(side * 21 * k, -124 * k, 12 * k, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = ink;
    ctx.beginPath();
    ctx.arc(side * 21 * k + tilt * 20 * k, -128 * k, 5.5 * k, 0, Math.PI * 2);
    ctx.fill();
  }
  // Shell: wide orange dome with a cream rim highlight and a few ink spots.
  ctx.fillStyle = shell;
  ctx.strokeStyle = ink;
  ctx.lineWidth = 6 * k;
  ctx.beginPath();
  ctx.ellipse(0, -64 * k, 66 * k, 40 * k, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = cream;
  ctx.lineWidth = 6 * k;
  ctx.beginPath();
  ctx.ellipse(0, -68 * k, 50 * k, 26 * k, 0, Math.PI * 1.12, Math.PI * 1.6);
  ctx.stroke();
  ctx.fillStyle = ink;
  for (const [x, y, r] of [[-24, -58, 5], [22, -54, 6], [2, -44, 4], [36, -72, 4]] as const) {
    ctx.beginPath();
    ctx.arc(x * k, y * k, r * k, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
  return true;
}

/**
 * The flying roadster skin: one static rear-overhead drawing (sprites/roadster.svg, same 256 frame and ground
 * reference as the runner atlases) hovering over its shadow, with flickering thrusters animated from `frame`.
 */
function drawRoadster(ctx: CanvasRenderingContext2D, state: SpriteState, frame: number, size: number, opts: { effect: boolean; shadow: boolean }): boolean {
  const img = brandImage('sprites/roadster.svg');
  if (!img) return false;
  const k = size / 256;
  const phase = (frame / 12) * Math.PI * 2;
  // Hover height: it sits lower while idle, lifts when racing, and dips briefly on stumbles.
  const lift = (state === 'idle' || state === 'waiting' ? 10 : state === 'stumble' ? 4 : 18) * k + Math.sin(phase) * 3 * k;
  const tilt = state === 'steer-left' ? -0.14 : state === 'steer-right' ? 0.14 : state === 'stumble' ? 0.1 * Math.sin(phase * 2) : 0;
  if (opts.shadow) {
    ctx.fillStyle = 'rgba(11,11,11,0.28)';
    ctx.beginPath();
    ctx.ellipse(0, 0, 84 * k * (1 - lift / (60 * k)), 14 * k, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  if (opts.effect) {
    for (const nx of [-24, 24]) {
      const len = (26 + 10 * Math.sin(phase * 3 + nx)) * k;
      const x = nx * k, y = -lift;
      const g = ctx.createLinearGradient(x, y, x, y + len);
      g.addColorStop(0, '#FFF2D8');
      g.addColorStop(0.35, '#EAAF43');
      g.addColorStop(1, 'rgba(236,103,58,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.moveTo(x - 10 * k, y);
      ctx.quadraticCurveTo(x, y + len * 1.2, x + 10 * k, y);
      ctx.fill();
    }
  }
  ctx.save();
  ctx.translate(0, -lift - 60 * k);
  ctx.rotate(tilt);
  ctx.drawImage(img, -128 * k, -142 * k, size, size);
  ctx.restore();
  return true;
}

/**
 * Draw sprite `state` at `frame` (0..11) with its ground reference at (0,0) of the current transform and a 256-unit
 * frame displayed at `size` CSS px. Returns false (draws nothing) if that state's atlas isn't decoded yet.
 */
export function drawRunnerSprite(ctx: CanvasRenderingContext2D, state: SpriteState, frame: number, size: number, opts: { effect: boolean; shadow: boolean }): boolean {
  if (skin === 'crab') return drawCrab(ctx, state, frame, size, opts);
  if (skin === 'roadster' && drawRoadster(ctx, state, frame, size, opts)) return true;
  const sp = runnerSprite(state);
  if (!sp) return false;
  const { meta, atlas } = sp;
  const k = size / meta.size; // scale = displaySize / 256; DPR is already in the context transform
  const [ax, ay] = meta.groundReference;
  const i = Math.max(0, Math.min(meta.rects.length - 1, frame | 0));
  const [sx, sy, sw, sh] = meta.rects[i];
  const dx = -ax * k;
  const dy = -ay * k;
  const prevSmooth = ctx.imageSmoothingQuality;
  ctx.imageSmoothingQuality = 'high';
  if (opts.shadow) {
    const sh0 = brandImage('sprites/runner-shadow.png');
    if (sh0) ctx.drawImage(sh0, dx, dy, size, size);
  }
  ctx.drawImage(atlas, sx, sy, sw, sh, dx, dy, size, size);
  if (opts.effect) {
    // Effect atlases share the 4×3 layout, 256 canvas and anchors with the body (1× atlases).
    const fx = runnerEffect(state);
    if (fx) ctx.drawImage(fx, (i % 4) * 256, Math.floor(i / 4) * 256, 256, 256, dx, dy, size, size);
  }
  ctx.imageSmoothingQuality = prevSmooth;
  return true;
}

/** Frame count and timing helpers for a decoded state (null while loading). */
export function spriteTiming(state: SpriteState): { loop: boolean; durationMs: number; frames: number } | null {
  const sp = runnerSprite(state);
  return sp ? { loop: sp.meta.loop, durationMs: sp.meta.durationMs, frames: sp.meta.rects.length } : null;
}

export type RunnerMode =
  | 'warmup'
  | 'starting'
  | 'run'
  | 'stumble'
  | 'overheat'
  | 'cool'
  | 'collect'
  | 'wait'
  | 'harvest'
  | 'forge'
  | 'finish'
  | 'rest'
  | 'replay';

export interface RunnerPose {
  t: number; // seconds
  phase: number; // stride phase 0..1
  speed: number; // 0..1.4 relative stride speed
  lean: number; // -1..1 (negative = leaning left)
  plant: number; // 0..1 foot-plant/brake intensity on direction change
  mode: RunnerMode;
  modeT: number; // seconds since mode began
  heat: number; // 0..1 overheat glow
  wet: number; // 0..1 water sheen
  holdingShoe: boolean;
}

const TAU = Math.PI * 2;

function limb(ctx: CanvasRenderingContext2D, pts: [number, number][], w: number, fill: string, highlight = false, glow = 0) {
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  if (glow > 0.05) {
    // Overheat: an ember rim around the ink body.
    ctx.strokeStyle = color.ember;
    ctx.globalAlpha = 0.55 * glow;
    ctx.lineWidth = w + 6;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = w + 2.6;
  ctx.stroke();
  ctx.strokeStyle = fill;
  ctx.lineWidth = w;
  ctx.stroke();
  if (highlight) {
    // A thin brush highlight so the ink body reads as volume, not a silhouette.
    ctx.save();
    ctx.translate(-0.8, -0.6);
    ctx.strokeStyle = color.inkBodyHi;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  }
}

function shoe(ctx: CanvasRenderingContext2D, x: number, y: number, lift: number, side: number, gold = false) {
  // Oversized trainer seen from behind: orange heel counter + thick cream sole; the sole tilts up when lifted.
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(side * 0.08);
  ctx.scale(1.3, 1.3);
  const showSole = lift > 0.35;
  ctx.fillStyle = gold ? color.gold : color.orange;
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.ellipse(0, 0, 5.6, showSole ? 5.2 : 4.4, 0, 0, TAU);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = gold ? color.goldSoft : color.cream;
  ctx.beginPath();
  if (showSole) ctx.ellipse(0, 1.2, 5.2, 3.8, 0, 0, TAU);
  else ctx.ellipse(0, 3.2, 5.4, 2, 0, 0, TAU);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

/**
 * Draw RUNNER with feet at (0,0) in the current transform; `s` scales the whole figure (≈ 1 → 78px tall).
 */
export function drawRunner(ctx: CanvasRenderingContext2D, pose: RunnerPose, s: number) {
  const { mode } = pose;
  ctx.save();
  ctx.scale(s, s);

  // Shadow
  ctx.fillStyle = 'rgba(23,21,15,0.20)';
  ctx.beginPath();
  ctx.ellipse(0, 2, 15, 5, 0, 0, TAU);
  ctx.fill();

  const running = mode === 'run' || mode === 'overheat' || mode === 'cool' || mode === 'collect' || mode === 'replay' || mode === 'starting';
  const jogInPlace = mode === 'wait' || mode === 'warmup';
  const stride = running ? 7.5 * Math.min(1.25, 0.6 + pose.speed * 0.5) : jogInPlace ? 2.2 : 0;
  const lift = running ? 5.5 : jogInPlace ? 3.2 : 0;
  const ph = pose.phase * TAU;

  let lean = pose.lean * 0.32;
  let pitch = 0;
  let bounce = running || jogInPlace ? Math.abs(Math.sin(ph)) * (running ? 2.2 : 1.4) : 0;
  if (mode === 'stumble') {
    const k = Math.min(1, pose.modeT / 0.35);
    pitch = Math.sin(Math.min(1, pose.modeT / 0.9) * Math.PI) * 0.55;
    lean += Math.sin(pose.modeT * 18) * 0.12 * (1 - Math.min(1, pose.modeT));
    bounce = -4 * k * (1 - Math.min(1, pose.modeT / 1.1));
  }
  if (mode === 'rest') {
    pitch = 0.45;
    bounce = Math.sin(pose.t * 2.2) * 0.8; // breathing
  }
  if (mode === 'warmup') {
    lean += Math.sin(pose.t * 1.6) * 0.18; // side stretch
  }

  ctx.rotate(lean);

  // Legs: alternate contact; plant pushes one leg out sideways on direction changes.
  const hipY = -30 - bounce;
  for (const side of [-1, 1]) {
    const legPh = ph + (side > 0 ? Math.PI : 0);
    const fwd = Math.sin(legPh) * stride;
    const up = Math.max(0, Math.cos(legPh)) * lift;
    const plantOut = pose.plant * (side === Math.sign(pose.lean || 1) ? 0 : 5);
    let fx = side * (6 + plantOut);
    let fy = -fwd * 0.8 - up;
    if (mode === 'rest') {
      fx = side * 8;
      fy = 0;
    }
    if (mode === 'harvest' || mode === 'finish' || mode === 'forge') {
      fx = side * 8;
      fy = -Math.max(0, Math.sin(pose.modeT * 9 + (side > 0 ? 1.5 : 0))) * 3;
    }
    if (mode === 'stumble') {
      fx = side * (8 + Math.sin(pose.modeT * 20 + side) * 3);
      fy = side > 0 ? -6 : 2;
    }
    const kx = (side * 6 + fx) / 2 + side * 2.4;
    const ky = (hipY + fy) / 2 - up * 0.4;
    limb(ctx, [[side * 5.5, hipY], [kx, ky], [fx, fy - 3]], 4.6, side < 0 ? color.inkBodyShade : color.inkBody, side > 0, pose.heat);
    shoe(ctx, fx, fy, up / Math.max(1, lift), side);
  }

  // Shorts (cream with orange side stripes)
  ctx.fillStyle = color.cream;
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(-9.5, hipY - 5);
  ctx.lineTo(9.5, hipY - 5);
  ctx.lineTo(10.5, hipY + 4);
  ctx.lineTo(1, hipY + 5);
  ctx.lineTo(0, hipY + 1);
  ctx.lineTo(-1, hipY + 5);
  ctx.lineTo(-10.5, hipY + 4);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = color.orange;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(-10, hipY + 3);
  ctx.lineTo(-9.4, hipY - 4);
  ctx.moveTo(10, hipY + 3);
  ctx.lineTo(9.4, hipY - 4);
  ctx.stroke();

  // Torso (singlet, back): shoulders counter-rotate with the stride.
  ctx.save();
  ctx.translate(0, hipY - 4);
  ctx.rotate(pitch * 0.4);
  const shoulderTwist = running ? Math.sin(ph) * 0.12 : 0;
  ctx.rotate(shoulderTwist * 0.4);
  const torsoH = 23 - pitch * 6;
  const heatTint = pose.heat;
  ctx.fillStyle = heatTint > 0.05 ? mix(color.orange, color.ember, heatTint) : pose.wet > 0.05 ? mix(color.orange, '#F3B79D', pose.wet) : color.orange;
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(-8.5, 0);
  ctx.lineTo(-11.5, -torsoH + 3);
  ctx.quadraticCurveTo(0, -torsoH - 3, 11.5, -torsoH + 3);
  ctx.lineTo(8.5, 0);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = color.cream;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(-9.8, -torsoH + 5);
  ctx.lineTo(-7.6, -1);
  ctx.moveTo(9.8, -torsoH + 5);
  ctx.lineTo(7.6, -1);
  ctx.stroke();
  // back bib
  ctx.fillStyle = color.cream;
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = 1;
  ctx.fillRect(-4.6, -torsoH + 7, 9.2, 7.6);
  ctx.strokeRect(-4.6, -torsoH + 7, 9.2, 7.6);
  ctx.fillStyle = color.ink;
  ctx.font = '900 6.4px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('01', 0, -torsoH + 11);

  // Arms
  const shY = -torsoH + 2.5;
  for (const side of [-1, 1]) {
    const armPh = ph + (side > 0 ? 0 : Math.PI);
    let hx: number;
    let hy: number;
    let ex: number;
    let ey: number;
    if (mode === 'harvest' || mode === 'finish') {
      const wave = Math.sin(pose.modeT * 10 + side) * 3;
      ex = side * 15;
      ey = shY - 10;
      hx = side * 14 + wave;
      hy = shY - 21;
    } else if (mode === 'forge') {
      if (side > 0) {
        ex = 14;
        ey = shY - 8;
        hx = 12;
        hy = shY - 20 - Math.abs(Math.sin(pose.modeT * 8)) * 4;
      } else {
        ex = -13;
        ey = shY + 6;
        hx = -10;
        hy = shY + 12;
      }
    } else if (mode === 'stumble') {
      ex = side * 16;
      ey = shY - 6 + Math.sin(pose.modeT * 22 + side) * 4;
      hx = side * 21;
      hy = shY - 14 + Math.cos(pose.modeT * 19 + side) * 5;
    } else if (mode === 'rest') {
      ex = side * 12;
      ey = shY + 9;
      hx = side * 9;
      hy = shY + 18;
    } else if (mode === 'collect' && side > 0 && pose.modeT < 0.6) {
      const k = pose.modeT / 0.6;
      ex = 14;
      ey = shY + 8 - k * 10;
      hx = 15;
      hy = shY + 16 - k * 30;
    } else if (pose.holdingShoe && side > 0) {
      ex = 14;
      ey = shY - 8;
      hx = 13;
      hy = shY - 20;
    } else {
      const swing = running ? Math.sin(armPh) * 6 : jogInPlace ? Math.sin(armPh) * 2.4 : 0;
      ex = side * 12.5;
      ey = shY + 7 - swing * 0.3;
      hx = side * 10.5;
      hy = shY + 12 - swing;
    }
    limb(ctx, [[side * 10, shY], [ex, ey], [hx, hy]], 3.8, side < 0 ? color.inkBodyShade : color.inkBody, side > 0, pose.heat);
    if (pose.holdingShoe && side > 0) shoe(ctx, hx, hy - 4, 1, 1, true);
  }

  // Head (from behind; profile peeks when leaning hard; faces camera to celebrate)
  const facing = mode === 'harvest' || mode === 'finish' || mode === 'forge' ? 1 : 0;
  const headX = pose.lean * 1.5;
  const headY = shY - 9 - pitch * 4;
  // ink tufts on the crown (drawn first so the head overlaps their roots)
  ctx.fillStyle = color.inkBody;
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = 1.4;
  ctx.lineJoin = 'round';
  const sway = Math.sin(pose.t * (running ? 12 : 3)) * (running ? 0.8 : 0.3);
  ctx.beginPath();
  ctx.moveTo(headX - 5.5, headY - 5.5);
  ctx.lineTo(headX - 6.5 + sway, headY - 12.5);
  ctx.lineTo(headX - 2, headY - 7.5);
  ctx.lineTo(headX + sway, headY - 14);
  ctx.lineTo(headX + 2.5, headY - 7.5);
  ctx.lineTo(headX + 6.5 + sway, headY - 12);
  ctx.lineTo(headX + 5.5, headY - 5);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  if (pose.heat > 0.05) {
    ctx.strokeStyle = color.ember;
    ctx.globalAlpha = 0.5 * pose.heat;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(headX, headY, 9.6, 0, TAU);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
  ctx.fillStyle = color.inkBody;
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.arc(headX, headY, 8.2, 0, TAU);
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = color.inkBodyHi;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(headX - 1, headY - 1, 5.6, Math.PI * 1.1, Math.PI * 1.55);
  ctx.stroke();
  if (pose.wet > 0.05) {
    ctx.fillStyle = color.waterSoft;
    ctx.globalAlpha = 0.8 * pose.wet;
    ctx.beginPath();
    ctx.arc(headX + 2.5, headY - 3, 1.3, 0, TAU);
    ctx.arc(headX - 3.2, headY + 1.5, 0.9, 0, TAU);
    ctx.fill();
    ctx.globalAlpha = 1;
  }
  if (facing) {
    // face: big cream eyes and a cream grin
    eye(ctx, headX - 3.1, headY + 0.2, 2.7, 0.6);
    eye(ctx, headX + 3.1, headY + 0.2, 2.7, 0.6);
    ctx.strokeStyle = color.cream;
    ctx.lineWidth = 1.3;
    ctx.beginPath();
    ctx.arc(headX, headY + 3.2, 2.8, 0.15 * Math.PI, 0.85 * Math.PI);
    ctx.stroke();
  } else if (Math.abs(pose.lean) > 0.35 || mode === 'stumble' || mode === 'wait') {
    // profile peek: one cream eye glances along the lean (or back at you while waiting)
    const sd = mode === 'wait' ? Math.sign(Math.sin(pose.t * 0.8)) || 1 : Math.sign(pose.lean);
    eye(ctx, headX + sd * 6.4, headY + 0.2, 2, sd * 0.6);
  }
  // cream headband with tails fluttering behind (toward +y on screen = backwards)
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = 4.4;
  ctx.beginPath();
  ctx.arc(headX, headY, 7.9, Math.PI * 1.05, Math.PI * 1.95);
  ctx.stroke();
  ctx.strokeStyle = color.cream;
  ctx.lineWidth = 2.6;
  ctx.stroke();
  const flap = Math.sin(pose.t * (running ? 14 : 5)) * (running ? 3 : 1.2);
  for (const k of [-1, 1]) {
    ctx.strokeStyle = color.ink;
    ctx.lineWidth = 3.2;
    ctx.beginPath();
    ctx.moveTo(headX + k * 1.5, headY + 2);
    ctx.quadraticCurveTo(headX + k * 6 + flap * k, headY + 9, headX + k * 4 + flap, headY + 15 + Math.abs(flap) * 0.4);
    ctx.stroke();
    ctx.strokeStyle = k > 0 ? color.orange : color.orangeDeep;
    ctx.lineWidth = 1.7;
    ctx.stroke();
  }
  ctx.restore();

  ctx.restore();
}

function eye(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, look: number) {
  ctx.fillStyle = color.cream;
  ctx.strokeStyle = color.ink;
  ctx.lineWidth = 0.9;
  ctx.beginPath();
  ctx.ellipse(x, y, r * 0.85, r, 0, 0, TAU);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = color.ink;
  ctx.beginPath();
  ctx.arc(x + look * r * 0.4, y + 0.2, r * 0.45, 0, TAU);
  ctx.fill();
}

export function mix(a: string, b: string, t: number): string {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const r = Math.round(((pa >> 16) & 255) * (1 - t) + ((pb >> 16) & 255) * t);
  const g = Math.round(((pa >> 8) & 255) * (1 - t) + ((pb >> 8) & 255) * t);
  const bl = Math.round((pa & 255) * (1 - t) + (pb & 255) * t);
  return `rgb(${r},${g},${bl})`;
}
